import './Card.scss'

import { cls } from '@react/utils/classname'
import { ReactNode, useContext } from 'react'

import { FilterableListContext } from '../FilterableListContext'
import { ItemType } from '../FilterableListTypes'
import { TagItem } from './TagItem'

type CardProps = {
  additionalTemplate?: ReactNode
  buttonLabel?: string
  children?: ReactNode
  classes?: string
  customTitle?: ReactNode
  item: ItemType
  showButton?: boolean
  showDescription?: boolean
  showMetaDate?: boolean
  showMetaTag?: boolean
  showMetaTimeToRead?: boolean
  showTag?: boolean
  showTitle?: boolean
  showViewsCount?: boolean
}

export const Card = ({
  additionalTemplate = null,
  buttonLabel,
  children,
  classes = '',
  customTitle = null,
  item,
  showButton = true,
  showDescription = true,
  showMetaDate = false,
  showMetaTag = false,
  showMetaTimeToRead = false,
  showTitle = true,
  showTag = true,
  showViewsCount = false,
}: CardProps) => {
  const { propsState, translationsState } = useContext(FilterableListContext)
  const metaDate = item.dateString || item.date?.split(' ')?.[0]
  const showMetaDateInfo = showMetaDate && Boolean(metaDate)
  const showSubInfo = showMetaDateInfo || showMetaTimeToRead || showViewsCount || showMetaTag

  let transformedUrl = item.url

  if (document.querySelector('.cmp-powerportal-main')) {
    const prependHttps = url => {
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        return `https://${url}`
      }
      return url
    }
    transformedUrl = prependHttps(transformedUrl)
  }

  const TitleComponent = () => {
    if (customTitle) {
      return customTitle
    }
    const HeadingTag = (propsState.seoHeadingElement || 'h3') as keyof JSX.IntrinsicElements
    return (
      <HeadingTag className="cmp-filterable-list__card-title h3">
        {propsState.icon && <span className={`icon--md icon-${propsState.icon}`} aria-hidden="true"></span>}
        {item.title}
      </HeadingTag>
    )
  }

  return (
    <div
      className={cls({
        'cmp-filterable-list__card': true,
        [classes]: true,
        'cmp-filterable-list__card--aspect-1-1': propsState.thumbnailAspectRatio === 'true',
      })}
    >
      <a className="cmp-filterable-list__card-link" href={transformedUrl} data-dm={`${propsState.componentName}.link`}>
        {children}
        {showTag && <div className="cmp-filterable-list__card-tag">{item.tagValue || item.eventType}</div>}
        <div className="cmp-filterable-list__card-content">
          {item.tags?.length > 0 && (
            <div className="cmp-filterable-list__card-tags">
              {item.tags.map(tag => (
                <TagItem key={tag} tag={tag} />
              ))}
            </div>
          )}
          {showTitle && <TitleComponent />}
          {showDescription && <p className="cmp-filterable-list__card-description">{item.description}</p>}
          <div className="cmp-filterable-list__card-info">
            {showSubInfo && (
              <div className="cmp-filterable-list__card-sub-info">
                {showMetaTag && propsState.type === 'news' && (item.tagValue || item.eventType) && (
                  <span className="cmp-filterable-list__card-meta--tag">{item.tagValue || item.eventType}</span>
                )}
                <span className="cmp-filterable-list__card-meta--info">
                  {showMetaDateInfo && <span className="cmp-filterable-list__card-meta--date">{metaDate}</span>}
                  {showMetaTimeToRead && Number(item.timeToRead) > 0 && (
                    <span className="cmp-filterable-list__card-meta--time-to-read">
                      {item.timeToRead} {translationsState.readingTime}
                    </span>
                  )}
                  {showViewsCount && (
                    <span className="cmp-filterable-list__card-meta--views-count">
                      {item.viewCounter} {item.viewCounter === 1 ? translationsState.viewLabel : translationsState.viewsLabel}
                    </span>
                  )}
                </span>
              </div>
            )}
            {showButton && (
              <div className="cmp-filterable-list__card-read-more">
                <span aria-label={buttonLabel} className="cmp-filterable-list__card-read-more--icon icon-keyboard_arrow_right icon--md"></span>
              </div>
            )}
          </div>
        </div>
      </a>
      {additionalTemplate}
    </div>
  )
}
